import React from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  ContextMenu,
  makeButtonWithMenu,
  BUTTON_VARIANT_SECONDARY,
  Flex,
  IconArrow1Down
} from 'mailup-web-components'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import { messageState } from 'Pages/OverviewMessage/recoil/state/message'
import evalCondition from 'Helpers/evalCondition'
import {
  MESSAGE_STR_TYPE_CONTENTSCAMPAIGN,
  MESSAGE_STR_TYPE_GENERIC
} from 'Constants/index'
import { notifyParent } from 'Helpers/notifyParent'
import {
  createLandingPagesCallback,
  initModalNotificationMessageCallback,
  sendingMessageCallback,
  downloadHtmlMessageCallback,
  downloadPdfMessageCallback
} from 'Pages/OverviewMessage/recoil/callbacks/callbacks'
import {
  modalConfirmCopy,
  modalConfirmCopyHtml,
  modalNoEdit
} from 'Pages/OverviewMessage/recoil/state/modal'
import styled from '@emotion/styled'

const EditMessageButton = () => {
  const intl = useIntl()
  const history = useHistory()
  const { messageType, isBee, id, idCampaign } = useRecoilValue(messageState)
  const canEdit = evalCondition('NOT uo:NONL')
  const canLanding = evalCondition('uo:PAGES')

  const isSendingMessage = useRecoilCallback(sendingMessageCallback)
  const setModalNoEdit = useSetRecoilState(modalNoEdit)

  const setModalConfirmCopy = useSetRecoilState(modalConfirmCopy)
  const setModalConfirmCopyHtml = useSetRecoilState(modalConfirmCopyHtml)

  const openModalNotificationMessage = useRecoilCallback(
    initModalNotificationMessageCallback
  )

  const createLandingPages = useRecoilCallback(createLandingPagesCallback, [])

  const downloadHtml = useRecoilCallback(downloadHtmlMessageCallback, [])
  const downloadPdf = useRecoilCallback(downloadPdfMessageCallback, [])

  const handleClickBtn = value => {
    switch (value) {
      case 'EDIT': {
        isSendingMessage(id).then(isSending => {
          if (!isSending) {
            if (isBee && messageType === MESSAGE_STR_TYPE_CONTENTSCAMPAIGN) {
              notifyParent('redirectMainFrame', [
                `App.aspx#/beePluginEditor/editCMP/${idCampaign}`
              ])
            }
            if (isBee && messageType !== MESSAGE_STR_TYPE_CONTENTSCAMPAIGN) {
              history.push(`/messages/email/bee/edit/${id}`)
            }
            if (!isBee) {
              notifyParent('redirectMainFrame', [
                `form_upd_newsletter.aspx?source=listemail&idNL=${id}`
              ])
            }
          } else {
            setModalNoEdit(true)
          }
        })
        break
      }

      case 'EDIT_HEADER': {
        notifyParent('redirectMainFrame', ['set_header.aspx'])
        break
      }

      case 'EDIT_FOOTER': {
        notifyParent('redirectMainFrame', ['set_footer.aspx'])
        break
      }

      case 'COPY': {
        setModalConfirmCopy(true)
        break
      }

      case 'COPY_HTML': {
        setModalConfirmCopyHtml(true)
        break
      }

      case 'NOTIFICATION': {
        openModalNotificationMessage(id)
        break
      }

      case 'TEXT': {
        history.push(`/messages/email/${id}/text-version`)
        break
      }

      case 'LANDING': {
        createLandingPages(id, idLP => {
          notifyParent('redirectMainFrame', [
            `databasebuilding_app_redirector.aspx?r=${encodeURIComponent(
              `/databasebuilding/landingpage/${idLP}/settings?op=generateFromEmail`
            )}`
          ])
        })
        break
      }

      case 'DOWNLOAD_HTML': {
        downloadHtml(id).then(fileContent => {
          if (fileContent) {
            const element = document.createElement('a')
            element.href = `data:text/html,${encodeURIComponent(fileContent)}`
            element.download = `${id}.html`
            document.body.appendChild(element)
            element.click()
            element.parentNode.removeChild(element)
          }
        })

        break
      }

      case 'DOWNLOAD_PDF': {
        downloadPdf(id).then(fileContent => {
          if (fileContent) {
            const element = document.createElement('a')
            element.href = `data:application/octet-stream;base64,${encodeURIComponent(
              fileContent
            )}`
            element.download = `${id}.pdf`
            document.body.appendChild(element)
            element.click()
            element.parentNode.removeChild(element)
          }
        })

        break
      }
    }
  }

  const ButtonComponent = () => (
    <Button className='right__button' kind={BUTTON_VARIANT_SECONDARY}>
      <IconArrow1Down />
    </Button>
  )

  const MenuComponent = () => {
    let items = []

    if (canEdit) {
      items = [
        ...items,
        {
          label: intl.formatMessage({ id: 'Load.MailupCommon.Edit' }),
          value: 'EDIT',
          onClick: handleClickBtn,
          'data-mu-test': 'action-edit'
        }
      ]
    }

    items = [
      ...items,
      {
        label: intl.formatMessage({ id: 'Load.SavedEmailWizard.EditHeader' }),
        value: 'EDIT_HEADER',
        onClick: handleClickBtn,
        'data-mu-test': 'action-edit-header'
      },
      {
        label: intl.formatMessage({ id: 'Load.SavedEmailWizard.EditoFooter' }),
        value: 'EDIT_FOOTER',
        onClick: handleClickBtn,
        'data-mu-test': 'action-edit-footer'
      }
    ]

    items = [
      ...items,
      {
        separator: true,
        label: intl.formatMessage({
          id: 'Load.EmailMessageOverview.DownloadHtml'
        }),
        onClick: handleClickBtn,
        value: 'DOWNLOAD_HTML',
        className: 'test-copy',
        'data-mu-test': 'action__DownloadHtml'
      }
    ]

    if (isBee) {
      items = [
        ...items,
        {
          label: intl.formatMessage({
            id: 'Load.EmailMessageOverview.DownloadPdf'
          }),
          onClick: handleClickBtn,
          value: 'DOWNLOAD_PDF',
          className: 'test-copy',
          'data-mu-test': 'action__DownloadPdf'
        }
      ]
    }

    if (messageType !== MESSAGE_STR_TYPE_CONTENTSCAMPAIGN) {
      items = [
        ...items,
        {
          label: intl.formatMessage({ id: 'Load.EmailMessageList.ActionCopy' }),
          value: 'COPY',
          onClick: handleClickBtn,
          'data-mu-test': 'action-copy'
        }
      ]
    }

    if (isBee && messageType !== MESSAGE_STR_TYPE_CONTENTSCAMPAIGN) {
      items = [
        ...items,
        {
          label: intl.formatMessage({
            id: 'Load.EmailMessageList.ActionCopyHtml'
          }),
          value: 'COPY_HTML',
          onClick: handleClickBtn,
          'data-mu-test': 'action-copy-html'
        }
      ]
    }

    if (messageType === MESSAGE_STR_TYPE_GENERIC) {
      items = [
        ...items,
        {
          separator: true,
          label: intl.formatMessage({
            id: 'Load.EmailMessageList.SetAsNotificationPage'
          }),
          value: 'NOTIFICATION',
          onClick: handleClickBtn,
          'data-mu-test': 'action-notification'
        }
      ]
    }
    // TEXT VERSION
    if (canEdit && messageType !== MESSAGE_STR_TYPE_CONTENTSCAMPAIGN) {
      items = [
        ...items,
        {
          separator: messageType !== MESSAGE_STR_TYPE_GENERIC,
          label: intl.formatMessage({
            id: 'Load.EmailCheckUp.TextVersionTab'
          }),
          value: 'TEXT',
          onClick: handleClickBtn,
          'data-mu-test': 'action-text'
        }
      ]
    }

    if (canLanding && messageType !== MESSAGE_STR_TYPE_CONTENTSCAMPAIGN) {
      items = [
        ...items,
        {
          separator:
            messageType !== MESSAGE_STR_TYPE_GENERIC ||
            !(canEdit && messageType !== MESSAGE_STR_TYPE_CONTENTSCAMPAIGN),
          label: intl.formatMessage({
            id: 'Load.EmailMessageList.ActionGenerateLanding'
          }),
          value: 'LANDING',
          onClick: handleClickBtn,
          'data-mu-test': 'action-landing'
        }
      ]
    }

    return (
      <ContextMenu items={items} data-mu-test='overview-message-actions-menu' />
    )
  }

  const ButtonWithMenu = makeButtonWithMenu(ButtonComponent, MenuComponent)

  return (
    <StyledFlex
      alignItems='center'
      width='100%'
      data-mu-test='overview-actions'
    >
      <Button
        className='left__button'
        kind={BUTTON_VARIANT_SECONDARY}
        onClick={() => handleClickBtn('EDIT')}
      >
        {intl.formatMessage({ id: 'Load.MailupCommon.Edit' })}
      </Button>
      <span />

      <ButtonWithMenu data-mu-test='overview-action-edit' direction='down' />
    </StyledFlex>
  )
}

export default EditMessageButton

const StyledFlex = styled(Flex)`
  .left__button {
    border-radius: 4px 0 0 4px;
    width: 100%;
  }

  .right__button {
    border-radius: 0 4px 4px 0;
    border-left: 0;
    margin-left: 0px;
    margin-right: 0px !important;
    > span {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .c-button-with-menu__menu {
    width: 222px;
    right: -8px;
  }
`
